<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import {useI18n} from "vue-i18n";
import {useFormatStore} from "@/stores/formatStore";
import {useUserStore} from "@/stores/userStore";
import {useTripsStore} from "@/stores/tripsStore";
import {useComparisonStore} from "@/stores/comparisonStore";
import {computed, onMounted, ref} from "vue";
import mapboxgl from "mapbox-gl";
import LayoutPopup from "@/components/Yacht/LayoutPopup.vue";
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Navigation, Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import LocationPin from "@/components/Map/LocationPin.vue";
import {useWindowSize} from "@vueuse/core";
import AddToFavoriteButton from "@/components/Button/AddToFavoriteButton.vue";
import TripAddModal from "@/components/TripCard/TripAddModal.vue";
import {SavedOffer} from "@/models/SavedOffer";

import YachtDeleteModal from "@/components/Yacht/YachtDeleteModal.vue"
import {useSearchStore} from "@/stores/searchStore";
import {useYachtStore} from "@/stores/yachtStore";
import {useRoute} from "vue-router";



import { defaultBoatImage } from "@/helpers/assetsUtils";

const {t} = useI18n(); // `t` for translating strings, `n` for numbers (currency)
const route = useRoute();

const formatStore = useFormatStore();
const tripsStore = useTripsStore();
const comparisonStore = useComparisonStore();
const userStore = useUserStore();
const searchStore = useSearchStore();
const yachtStore = useYachtStore();
const showAddTripModal = ref(false);
const showDeleteModal = ref(false);
const yachtToDelete = ref<number | string | null>(null);
const yachtToDeleteName = ref<string | null>(null);
const amenities = ref([]);

const props = defineProps({
  yachts: Object,
  yachtsToCompare: Array,
  isSavedOffer: { type: Boolean, default: false },
  isTripAdmin: { type: Boolean, default: false }
});

const emit = defineEmits<{
  (e: 'on-view-details-click', payload: string | { yachtUuid: string | number }, price?: number, finalPrice?: number): void;
}>();

function handleCompare(yacht) {
  comparisonStore.onCompare(yacht);
}
const showModelLayout = ref<boolean>(false);
const selectLayoutImage = ref<string>('');
const selectLayoutAlt = ref<string>('');

const layoutImage = computed(() => {
  if (selectLayoutImage.value != '') {
    return selectLayoutImage.value;
  }
  return '';
});

function layoutAlt(modelName) {
  if (selectLayoutImage.value != '') {
    selectLayoutAlt.value = modelName;
  }
  return '';
}

function mainImageCarousel(mainImage, altImages) {
  let result = [{id: 1999999, name: mainImage}, ...altImages];

  return result;
}

const { width } = useWindowSize();
const isMobile = computed(() => {
  const value = width.value <= 885;
  return value;
});

const trimTextIfMobile = (text, maxLength) => {
  return isMobile.value && text.length > maxLength ? text.substring(0, maxLength) + ".." : text;
};

const selectedYachtForModal = ref<null | {
  yacht: { id: number | string; name: string };
  price: number;
  finalPrice: number;
}>(null);
const yachtLocationForModal = ref<any>(null);

const isYachtPage = computed(() => route.name === 'YachtPage' || route.meta?.yachtPage === true);


async function openAddTripModal(y: any) {
  const yachtData = {
    yacht: { id: y.yacht.id, name: y.yacht.name },
    price: y.price,
    discount: y.discount,
    finalPrice: y.finalPrice,
    passengers: searchStore.selectedFilters.passengers,
    selectedAmenities: []
  };

  let optionalAmenities = [];
  if (isYachtPage.value) {

    console.log("Inside yacht page")
    // if we are in the yacht page take amenities from availability
    optionalAmenities = yachtStore.optionalAmenities;
  }
  else {
    console.log("Outside yacht page")
    // if we are out of the yacht page, take amenities from the yacht
    const amenitiesData = await yachtStore.fetchAmenitiesForFavorites(y.yacht.slug)
    optionalAmenities = amenitiesData.optional
  }
  
  amenities.value = optionalAmenities;
  selectedYachtForModal.value = yachtData;
  yachtLocationForModal.value = y?.locationFrom ?? null;
  showAddTripModal.value = true;
}

function openDeleteModal(y: any) {
  yachtToDelete.value = y.uuid; // offer uuid
  yachtToDeleteName.value = y?.yacht?.name ?? null;
  showDeleteModal.value = true;
}

const tripUuid = computed(() => route.params.id as string | undefined);
const isDeletingOffer = ref(false);

async function handleDelete() {
  try{
    if (!yachtToDelete.value || !tripUuid.value) return;
    isDeletingOffer.value = true;
    await tripsStore.deleteTripOffer(tripUuid.value, yachtToDelete.value);
    showDeleteModal.value = false;
    yachtToDelete.value = null;
    await tripsStore.loadTrip(route.params.id)
  } catch (error) {
    console.error(`Failed to delete offer ${yachtToDelete.value}:`, error);
  } finally {
    isDeletingOffer.value = false;
  }
}

const isCancelled = (offer) => offer.state === 'cancel' || offer.state === 'lost';
const loginEnabled = import.meta.env.VITE_LOGIN_ENABLED

</script>
<template>

  <LayoutPopup :show="showModelLayout"
               extraClasses="flex lg:min-h-[65vh] min-h-[60vh] max-w-[50vw] min-w-45vw overflow-hidden"
               @close="showModelLayout = false">
    <img v-if="layoutImage" class="p-8 justify-center align-center object-cover" :src="layoutImage"
         :alt="selectLayoutAlt">
    <div v-else class="flex flex-col self-center font-semibold responsive-title-6 mt-8">
      <div>Sorry there is no layout for the selected yacht</div>
      <img class="w-[65%] mt-6 self-center align-center object-cover"
           src="@/assets/images/Cosmos_yachting_illustration_2.svg" alt="not-found">
    </div>
  </LayoutPopup>
<!--  <div v-for="yacht in yachts"-->
<!--       :key="`${yacht.yacht.id}_${yacht.locationFrom?.id || 'unknown'}`"-->
<!--       @click="$emit('on-view-details-click', isSavedOffer ? { yachtUuid: (yacht.uuid || yacht.yacht?.uuid || yacht.yacht?.id) } : yacht.yacht.slug, yacht.price, yacht.finalPrice)"-->
<!--       :class="`${comparisonStore.isYachtSelected(yacht.yacht.id) ? 'users-selected-yacht' : '' } border width-fill rounded-8xl flex flex-col gap-2 relative card hoverable-card`">-->

  <div
      v-for="yacht in yachts"
      :key="`${yacht.yacht.id}_${yacht.locationFrom?.id || 'unknown'}`"
      @click="yacht.state !== 'cancel' && $emit(
    'on-view-details-click',
    isSavedOffer
      ? { yachtUuid: (yacht.uuid || yacht.yacht?.uuid || yacht.yacht?.id) }
      : yacht.yacht.slug,
    yacht.price,
    yacht.finalPrice
  )"
      :class="[
    'border width-fill rounded-8xl flex flex-col gap-2 relative card hoverable-card',
    comparisonStore.isYachtSelected(yacht.yacht.id) ? 'users-selected-yacht' : '',
    isCancelled(yacht) ? 'cursor-not-allowed pointer-events-none' : ''
  ]"
  >

  <!-- Deal of the day card -->
    <div v-if="!isSavedOffer && yacht.isDeal"
         class="absolute flex flex-col pt-1 text-primary-darkBlue z-10 -top-1/30 -left-px  responsive-body-small border border-notifications-completed rounded-8xl rounded-bl-none bg-notifications-pending font-bold">

      <div class="flex gap-1 align-center px-3 py-1.5 rounded-t-[10px]">
        <img class="h-4" src="@/assets/icons/svg/Clock black_16x16.svg" alt="clock"/>
        {{ t('dealOfTheDay') }}
        
      </div>
    </div>
    <div v-if="!isSavedOffer && yacht.yacht.charterType.id == 2"
         :class="`absolute flex flex-col pt-1 text-primary-darkBlue z-5 -top-1/30  responsive-body-small ${ yacht.isDeal ?'left-36 rounded-r-[10px]' : '-left-px rounded-8xl rounded-bl-none'} border border-notifications-completed bg-gradients-aliceBlue font-bold`">
      <div class="flex gap-1 align-center px-3 py-1.5 rounded-t-[10px]">
        <img class="w-4 h-4" src="@/assets/boat.png" alt="location"/>
        {{ t('boatDetails.crewed') }}
      </div>
    </div>
    <!-- Deal of the day card END -->

    <div
        class="flex flex-col gap-4 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-8xl p-5 min-h-[360px]"
        :class="[
            `${yacht.bundle}-bg ${yacht.isDeal || yacht.yacht.charterType.id == 2 ? 'pt-7' : '' }`,
            isCancelled(yacht) ? 'opacity-30' : ''
        ]">
      <div class="flex flex-col gap-2">
        <div class="flex justify-between items-center">
          <div class=" flex flex-col gap-2 w-3/6" @click.stop>
            <el-popover
                placement="top"
                trigger="hover"
                :content="t('searchResults.viewLocation')"
                :popper-style="{'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700', 'margin-left': '-40px'}"
                popper-class="text-black pointer-events-none"
            >
              <template #reference>
                <div class="flex cursor-pointer hover:opacity-60 transition-opacity">
                  <LocationPin v-model="yacht.locationFrom" class="flex gap-1" />
                </div>
              </template>
            </el-popover>
          </div>
          <div class="flex gap-2 relative" @click.stop>
            <template v-if="!isSavedOffer">

              <el-popover
                  placement="top"
                  :width="120"
                  trigger="hover"
                  content="View Yacht Layout"
                  :popper-style="{ 'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700'}"
                  popper-class="text-black pointer-events-none"
              >
                <template #reference>

                  <button
                      :class="`border z-10 px-4 py-1.5 border-grey rounded-12xl h-auto`"
                      @click="selectLayoutImage = yacht.yacht.layoutImage?.url; layoutAlt(yacht.yacht.model.name); showModelLayout = true;"
                  >
                    <img class="hover:opacity-80 w-4 h-4 transition-opacity"
                         src="@/assets/icons/svg/Boat_plan_24x24.svg" alt="image"/>
                  </button>
                </template>
              </el-popover>

              <el-popover
                  placement="top"
                  :width="150"
                  trigger="hover"
                  :content="comparisonStore.yachtsToCompare.some(compBoat => compBoat.yacht.id === yacht.yacht.id)
                  ? t('removeFromComparison')
                  : (comparisonStore.yachtsToCompare.length >= 4 ? t('maxYachtsReached') : t('addToComparison'))"
                  :popper-style="{ 'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700'}"
                  popper-class="text-black pointer-events-none"
              >
                <template #reference>
                  <button
                      :class="`border z-10 transition-[border-color]
                      ${comparisonStore.yachtsToCompare.some(compBoat => compBoat.yacht.id === yacht.yacht.id) ? 'border-primary-blue' : 'border-grey'} px-4 py-1.5 rounded-12xl h-auto`"
                      @click="comparisonStore.yachtsToCompare.length >= 4 && !comparisonStore.yachtsToCompare.some(compBoat => compBoat.yacht.id === yacht.yacht.id) ? null : handleCompare(yacht)">
                    <img
                        :class="comparisonStore.yachtsToCompare.some(compBoat => compBoat.yacht.id === yacht.yacht.id) ? 'comparison-icons-selected' : ''"
                        src="@/assets/icons/svg/Comparison_16x16.svg" alt="comparison"/>
                  </button>
                </template>
              </el-popover>
              
              <AddToFavoriteButton 
                  v-if="loginEnabled === 'true'"
                  v-model:show-add-trip-modal="showAddTripModal"
                  :yacht="yacht"
                  :date-from="searchStore.selectedFilters.dateFrom"
                  :date-to="searchStore.selectedFilters.dateTo"
                  :yacht-location="yacht.locationFrom.id"
                  :amenities="amenities"
                  :discount="yacht.discount"
                  :passengers="searchStore.selectedFilters.passengers"
                  popup-size="small"
                  @update:showAddTripModal="val => { if (val) openAddTripModal(yacht) }"
              />

            </template>
            <template v-if="isSavedOffer && !isCancelled(yacht)">
              <button
                  @click.stop="openDeleteModal(yacht)"
                  class="hover:opacity-70 transition-opacity"
                  aria-label="Delete offer"
              >
                <img src="@/assets/icons/svg/Delete_16x16.svg" :alt="t('delete')" />
              </button>
            </template>
          </div>
        </div>
        <div class="flex h-11 items-center">
          <div class="responsive-title-6 font-bold yacht-name">
            {{ yacht.yacht.model.name || '' }} • {{ yacht.yacht.year }} | {{ yacht.yacht.name }}
          </div>
        </div>
      </div>

      <div class="yacht-content relative flex flex-col sm:flex-row items-center">
        <!-- Boat Image -->
        <div class="yacht-image-content h-52 w-full sm:w-3/5" @click.stop>
          <Swiper
              :modules="[Navigation, Pagination]"
              class="rounded-8xl h-full"
              :spaceBetween="10"
              :slidesPerView="1"
              :pagination="{ clickable: true }"
              :navigation="true"
          >
            <!-- Loop through images -->
            <SwiperSlide
                v-for="image in (yacht.yacht.mainImage?.name ? mainImageCarousel(yacht.yacht.mainImage.name, yacht.yacht.images.slice(1,3)) : [{ id: 0, name: defaultBoatImage }])"
                :key="image.id"
            >
              <div class="w-full contents">
                <img
                    class="h-full w-full object-cover object-center cursor-pointer"
                    :src="image.name"
                    @click.stop
                    @error="($event) => $event.target.src = defaultBoatImage"
                />
              </div>
            </SwiperSlide>
          </Swiper>
        </div>


        <!-- Items Row -->
        <!--        <div class="flex flex-col gap-2 ml-auto justify-between w-[30%] boatImage-container">-->
        <div
            class="flex flex-wrap gap-2 justify-space-between w-full mt-4 sm:gap-2 sm:mt-0 sm:justify-between sm:mt-0 sm:flex-col sm:ml-auto sm:w-3/10">
          <div class="flex flex-col sm:flex-row items-center gap-2 ">
            <img src="@/assets/icons/svg/Bed_24x24.svg" alt="beds" class="mb-1 sm:mb-0"/>
            <span class="responsive-body-xsmall">{{ yacht.yacht.berths }} {{ t('boatDetails.berths') }}</span>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2 ">
            <img src="@/assets/icons/svg/Cabin_24x24.svg" alt="cabins" class="mb-1 sm:mb-0"/>
            <span class="responsive-body-xsmall">{{ yacht.yacht.cabins }} {{ t('boatDetails.cabins') }}</span>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2 ">
            <img src="@/assets/icons/svg/WC_24x24.svg" alt="bathroom" class="mb-1 sm:mb-0"/>
            <span class="responsive-body-xsmall">{{ yacht.yacht.wc }} {{ t('boatDetails.wc') }}</span>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2 ">
            <img src="@/assets/icons/svg/Length_24x24.svg" alt="length" class="mb-1 sm:mb-0"/>
            <span class="responsive-body-xsmall hidden sm:block">{{ formatStore.formatLength(yacht.yacht.length) }}</span>
            <span class="responsive-body-xsmall block sm:hidden">{{ (yacht.yacht.length).toFixed(2) }} m</span>
            <span class="responsive-body-xsmall block sm:hidden">({{
                (yacht.yacht.length * 3.28084).toFixed(2)
              }} ft)</span>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2 ">
            <img src="@/assets/icons/svg/Engine_24x24.svg" alt="engine" class="mb-1 sm:mb-0"/>
            <span class="responsive-body-xsmall">
            {{ yacht.yacht.engine ? trimTextIfMobile(yacht.yacht.engine, 8) : '-' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div 
        class="flex flex-col place-content-between px-5 py-2.5 gap-y-1"
        :class="isCancelled(yacht) ? 'opacity-30' : ''"
    >
      <div class="flex">
        <div class="w-full justify-between" v-if="yacht.isDeal">
          <div class="flex align-center gap-2 pt-[1px] pb-[0.5px]">
            <div v-if="yacht.price != yacht.finalPrice"
                 class=" responsive-body-small bg-orange-600 text-white w-fit h-fit py-px px-2 rounded-15xl">
              -{{ Math.round(yacht.discount) }}%
            </div>
            <span class="responsive-body-xxsmall text-orange-600">{{ t('dealOfTheDayUppercase') }}</span>
          </div>
        </div>
        <div class="flex align-center gap-2 pt-[1px] pb-[0.5px]" v-else>
          <div v-if="yacht.price != yacht.finalPrice"
               class=" responsive-body-small bg-primary-blue text-white w-fit h-fit py-px px-2 rounded-15xl">
            -{{ Math.round(yacht.discount) }}%
          </div>
          <span v-if="yacht.price != yacht.finalPrice" class="responsive-body-xsmall text-primary-blue">{{
              t('specialOfferUppercase')
            }}</span>
        </div>
      </div>

      <div class="flex w-full">
        <div class="w-full"
             :class="{'flex items-end' : yacht.price == yacht.finalPrice} ">
          <div class="flex justify-between w-full items-center mt-0.5">
            <div>
              <div class="flex">

                <div class="flex align-baseline gap-2">
                  <span class="responsive-title-4 font-semibold">{{ formatStore.formatCurrency(yacht.finalPrice) }}</span>

                </div>
              </div>
              <div>
                <div v-if="yacht.price != yacht.finalPrice" class="flex flex-col gap-1 pt-[1.5px] pb-[0.5px]">
                        <span class="responsive-body-xsmall text-grey line-through">
                          {{ formatStore.formatCurrency(yacht.price) }}</span>
                </div>
              </div>
            </div>
            <div>
              <CustomButton
                  type="black"
                  :rounded="true"
                  :outlined="false"
                  class="w-100 px-5 py-2"
                  @click="$emit('on-view-details-click', isSavedOffer ? { yachtUuid: (yacht.uuid || yacht.yacht?.uuid || yacht.yacht?.id) } : yacht.yacht.slug, yacht.price, yacht.finalPrice)">
                {{ t('viewDetails') }}
              </CustomButton>
            </div>
          </div>
        </div>


      </div>


    </div>
    <div v-if="isCancelled(yacht)" class="absolute right-4 top-4">
        <div class="responsive-body-xsmall rounded-11xl flex px-3 py-1 w-fit flex-row gap-1.5 justify-center bg-[#ff9d9d]">
          <img src="@/assets/icons/svg/Cancelled_16x16.svg" alt="" width="16" height="16" />
          <span style="line-height:17px">{{ t('tripCard.cancelled') }}</span>
        </div>
    </div>
  </div>
  <TripAddModal
      v-model:show="showAddTripModal"
      @on-trip-created="tripsStore.getAll"
      :date-from="searchStore.selectedFilters.dateFrom"
      :date-to="searchStore.selectedFilters.dateTo"
      :yacht-location="yachtLocationForModal"
      :yacht="selectedYachtForModal"
      :amenities="amenities"
      :selected-amenities="[]"
  />

  <YachtDeleteModal
      v-model:show="showDeleteModal"
      :yacht="{ id: yachtToDelete, name: yachtToDeleteName }"
      :is-deleting="isDeletingOffer"
      @on-delete-click="handleDelete"
      @on-close="showDeleteModal = false"
  />
</template>

<style scoped>

.swiper-button-next, .swiper-button-prev {
  color: white !important;
  background-color: transparent !important;
}


.swiper-pagination-bullet-active {
  background-color: white !important;
  opacity: 1;
}


.popper-custom {
  text-align: center !important;
}


.users-selected-yacht .selected-offer__border {
  outline: solid 2px #3B82F6 !important;
}

.border-primary-blue {
  border-color: #3B82F6 !important;
  background: #3B82F6 !important;
}


.comparison-icons-selected {
  filter: invert(1);
}

.border-grey:hover {
  border-color: #000000 !important;
}

.premium-bg {
  --tw-gradient-to: #d0baff var(--tw-gradient-to-position) !important;
}

.standard-bg {
  --tw-gradient-to: #a4e8db var(--tw-gradient-to-position) !important;
}

.economy-bg {
  --tw-gradient-to: #ffda9d var(--tw-gradient-to-position) !important;
}

.ship-bg-mask {
  mask-image: url("@/assets/images/mask/mask.svg");
  mask-repeat: no-repeat;
  mask-size: cover;
}

.popup-enter-active {
  animation: slide-opacity 0.2s ease-out reverse;
}

.popup-leave-active {
  animation: slide-opacity 0.2s ease-in;
}

.yacht-name {
  /* Trim the yacht name to 2 lines only */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  white-space: normal;
  word-wrap: break-word;
}
.hoverable-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.hoverable-card:hover {
  transform: scale(1.01);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

:deep(.swiper-pagination) {
  text-align:start;
  margin-left: 5px;
}
:deep(.swiper-pagination-bullet-active) {
  background: white;
}
:deep(.swiper-pagination-bullet){
  background: white;
}


:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 35px ;
  height: 35px;
  background-color: white ;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 16px ;
  color: black;
  font-weight: bold;
}

:deep(.swiper-button-prev) {
  left: 10px;
}

:deep(.swiper-button-next) {
  right: 10px;
}

:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.hoverable-card:hover :deep(.swiper-button-prev),
.hoverable-card:hover :deep(.swiper-button-next) {
  opacity: 1;
  visibility: visible;
}



</style>